import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getGameSocket, lobbySocket } from '@api/socket.js';
import { getPersistentUserId } from '@api/userService.js';
import { getChampionSuggestions } from '@api/championService.js';
import {logDev} from "@utils/logDev.js";

export function useImageGameController() {
    const { id: lobbyCode } = useParams();
    const persistentId = useMemo(() => getPersistentUserId(), []);
    const navigate = useNavigate();
    const socket = useMemo(() => getGameSocket('image'), []);

    // State
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [scoreboard, setScoreboard] = useState([]);
    const [isCreator, setIsCreator] = useState(false);
    const [isSpectator, setIsSpectator] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(100);
    const [selectedAnswer, setSelectedAnswer] = useState('');
    const [revealed, setRevealed] = useState(false);
    const [correctAnswer, setCorrectAnswer] = useState(null);
    const [gameOver, setGameOver] = useState(false);
    const [showFinalScreen, setShowFinalScreen] = useState(false);
    const [imageDifficulties, setImageDifficulties] = useState([]);
    const [pixelationStrength, setPixelationStrength] = useState(0);
    const [tempPixelationStrength, setTempPixelationStrength] = useState(0);
    const [answerLocked, setAnswerLocked] = useState(false);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);

    // Join flow
    const [showJoinDialog, setShowJoinDialog] = useState(false);
    const [joinError, setJoinError] = useState('');
    const [forcePassword, setForcePassword] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Suggestions
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // Refs
    const offsetRef = useRef(0);
    const timerRef = useRef(null);
    const inputRef = useRef(null);

    // Refs für State-Werte die im Countdown-Callback benötigt werden (vermeidet Timer-Resets)
    const selectedAnswerRef = useRef(selectedAnswer);
    const answerLockedRef = useRef(answerLocked);
    const revealedRef = useRef(revealed);
    const isSpectatorRef = useRef(isSpectator);
    const handleSubmitRef = useRef(null);
    useEffect(() => { selectedAnswerRef.current = selectedAnswer; }, [selectedAnswer]);
    useEffect(() => { answerLockedRef.current = answerLocked; }, [answerLocked]);
    useEffect(() => { revealedRef.current = revealed; }, [revealed]);
    useEffect(() => { isSpectatorRef.current = isSpectator; }, [isSpectator]);

    const startCountdown = useCallback((startTs, dur) => {
        clearInterval(timerRef.current);
        const tick = () => {
            const nowClient = Date.now() + offsetRef.current;
            const msLeft = startTs + dur - nowClient;
            const secLeft = Math.ceil(msLeft / 1000);
            const progressPercent = Math.max(0, (msLeft / dur) * 100);
            setTimeLeft(secLeft > 0 ? secLeft : 0);
            setProgress(progressPercent);
            if (msLeft <= 500 && selectedAnswerRef.current && !answerLockedRef.current && !revealedRef.current && !isSpectatorRef.current) {
                handleSubmitRef.current?.(selectedAnswerRef.current);
            }
            if (msLeft <= 0) clearInterval(timerRef.current);
        };
        tick();
        timerRef.current = setInterval(tick, 50);
    }, []); // Keine State-Dependencies – Refs werden stattdessen verwendet

    // handleSubmit wird nach startCountdown definiert und über Ref verfügbar gemacht
    const handleSubmit = useCallback((answer) => {
        logDev('[handleSubmit] Versuche Antwort zu senden:', {
            socketId: socket.id,
            connected: socket.connected,
            answer,
            timeLeft,
            revealed,
            isSpectator,
            answerLocked,
        });

        if (timeLeft === 0 || revealed || isSpectator || !answer || answerLocked) {
            logDev('[handleSubmit] Abgebrochen wegen:', {
                timeLeft,
                revealed,
                isSpectator,
                noAnswer: !answer,
                answerLocked
            });
            return;
        }

        if (!socket.connected) {
            console.warn('[handleSubmit] Socket nicht verbunden!');
            return;
        }
        socket.emit('submitAnswer', { lobbyCode, questionIndex: currentIndex, answer, persistentId });
        setAnswerLocked(true);
    }, [timeLeft, revealed, isSpectator, lobbyCode, currentIndex, persistentId, socket, answerLocked]);

    // Ref aktuell halten damit startCountdown immer die neueste Version nutzt
    useEffect(() => { handleSubmitRef.current = handleSubmit; }, [handleSubmit]);

    const handleInputChange = useCallback((e) => {
        const newAnswer = e.target.value;
        setSelectedAnswer(newAnswer);
        setHighlightedIndex(-1);
        if (newAnswer.length >= 2) {
            const matches = getChampionSuggestions(newAnswer);
            setSuggestions(matches);
            setShowSuggestions(matches.length > 0);
        } else {
            setSuggestions([]);
            setShowSuggestions(false);
        }
    }, []);

    const handleKeyDown = useCallback((e) => {
        if (!showSuggestions || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev - 1 + suggestions.length) % suggestions.length);
        } else if (e.key === 'Enter') {
            if (highlightedIndex >= 0) {
                const { name } = suggestions[highlightedIndex];
                setSelectedAnswer(name);
                setShowSuggestions(false);
                handleSubmit(name);
            } else if (selectedAnswer) {
                handleSubmit(selectedAnswer);
            }
        }
    }, [showSuggestions, suggestions, highlightedIndex, selectedAnswer, handleSubmit]);

    const handleSuggestionClick = useCallback(({ name }) => {
        setSelectedAnswer(name);
        setShowSuggestions(false);
        handleSubmit(name);
    }, [handleSubmit]);

    const nextQuestion = useCallback(() => {
        if (currentIndex < questions.length - 1) {
            socket.emit('questionChange', {
                lobbyCode,
                questionIndex: currentIndex + 1
            });
        }
    }, [currentIndex, questions.length, lobbyCode, socket]);

    const handleLeaveLobby = useCallback(() => {
        lobbySocket.emit('leaveLobby', { lobbyCode, persistentId }, () => navigate('/'));
    }, [lobbyCode, persistentId, navigate]);

    const handleReturnToLobby = useCallback(() => {
        lobbySocket.emit('resetGame', { lobbyCode });
        navigate(`/lobby/${lobbyCode}`);
    }, [lobbyCode, navigate]);

    // Game events
    useEffect(() => {
        // 1) Spielstart abonnieren (für alle Teilnehmer, inkl. Zuschauer)
        const onGameStarting = ({questions: qs, startTimestamp, duration: dur, imageDifficulties: diffs, pixelationStrength: pix, isCreator: creatorFlag}) => {
            setQuestions(qs);
            setImageDifficulties(diffs);
            setPixelationStrength(pix);
            setIsCreator(creatorFlag);
            setIsSpectator(false);
            setDuration(dur);
            setGameOver(false);
            setShowFinalScreen(false);
            startCountdown(startTimestamp, dur);
            setIsLoading(false);
        };
        const onQuestionChanged = ({ questionIndex, startTimestamp, duration: dur, serverTime }) => {
            offsetRef.current = serverTime - Date.now();
            setSelectedAnswer('');
            setRevealed(false);
            setAnswerLocked(false);
            setCorrectAnswer(null);
            setCurrentIndex(questionIndex);
            setScoreboard(prev =>
                prev.map(p => ({
                    ...p,
                    lastAnswer: null,
                }))
            );
            setDuration(dur);
            setPixelationStrength(tempPixelationStrength);
            startCountdown(startTimestamp, dur);
            setAnswerLocked(false); // Reset answerLocked bei neuer Frage
        };
        const onScoreboardUpdate = (sb) => {
            setScoreboard(sb);
            // Prüfen ob der aktuelle Spieler eine Antwort hat
            const myEntry = sb.find(p => p.persistentId === persistentId);
            if (myEntry?.lastAnswer) {
                setAnswerLocked(true);
                setSelectedAnswer(myEntry.lastAnswer);
            }
        };
        const onRevealAnswer = ({ questionIndex, correctAnswer: ca }) => {
            if (questionIndex === currentIndex) {
                setPixelationStrength(0);
                setCorrectAnswer(ca);
                setRevealed(true);
            }
        };
        const onGameOver = () => setGameOver(true);

        socket.on('gameStarting', onGameStarting);
        socket.on('questionChanged', onQuestionChanged);
        socket.on('scoreboardUpdate', onScoreboardUpdate);
        socket.on('revealAnswer', onRevealAnswer);
        socket.on('gameOver', onGameOver);

        return () => {
            socket.off('gameStarting', onGameStarting);
            socket.off('questionChanged', onQuestionChanged);
            socket.off('scoreboardUpdate', onScoreboardUpdate);
            socket.off('revealAnswer', onRevealAnswer);
            socket.off('gameOver', onGameOver);
        };
    }, [socket, lobbyCode, currentIndex, startCountdown, tempPixelationStrength]);

    const applyFullGameState = useCallback((gameState, scoreboard, reveal) => {
        setQuestions(gameState.questions);
        setCurrentIndex(gameState.questionIndex);
        setImageDifficulties(gameState.imageDifficulties || []);
        setPixelationStrength(gameState.pixelationStrength || 0);
        setTempPixelationStrength(gameState.pixelationStrength || 0);
        setDuration(gameState.duration);
        setIsCreator(gameState.isCreator);
        setIsSpectator(gameState.isSpectator);
        setScoreboard(scoreboard || []);
        offsetRef.current = Date.now() - gameState.startTimestamp;
        startCountdown(gameState.startTimestamp, gameState.duration);
        logDev('[Game] Game state applied:', gameState);
        if (gameState.currentAnswers) {
            setScoreboard(
                (scoreboard || []).map(player => ({
                    ...player,
                    lastAnswer: gameState.currentAnswers?.[player.persistentId] || null
                }))
            );
        }
        if (gameState.selectedAnswer) {
            setSelectedAnswer(gameState.selectedAnswer);
            setAnswerLocked(true);
        }
        if (reveal) {
            setCorrectAnswer(reveal.correctAnswer);
            setRevealed(true);
        }
        setIsLoading(false);
    }, [startCountdown]);

    const reconnect = useCallback(() => {
        logDev('[reconnect] Starte Reconnect...', {
            socketId: socket.id,
            connected: socket.connected
        });
        socket.emit('joinGame', { lobbyCode });
        socket.emit('reconnectToGame', { lobbyCode, persistentId }, response => {
            logDev('[reconnect] Server-Antwort:', response);
            if (!response.success) return navigate('/');
            if (response.requiresJoin) {
                setForcePassword(response.requiresPassword || false);
                setShowJoinDialog(true);
                setIsLoading(false);
                return;
            }
            const { serverTime, gameState, scoreboard: sb, reveal } = response;
            offsetRef.current = serverTime - Date.now();
            setScoreboard(sb);
            setQuestions(gameState.questions);
            setImageDifficulties(gameState.imageDifficulties);
            setTempPixelationStrength(gameState.pixelationStrength);
            setPixelationStrength(gameState.pixelationStrength);
            setIsSpectator(!!gameState.isSpectator);
            setIsCreator(gameState.isCreator);
            setGameOver(false);
            setShowFinalScreen(false);
            setDuration(gameState.duration);
            if (gameState.selectedAnswer) {
                setSelectedAnswer(gameState.selectedAnswer);
                setAnswerLocked(true);
            }
            if (gameState.hasStarted && typeof gameState.remainingTime === 'number') {
                setCurrentIndex(gameState.questionIndex);

                const serverRemainingMs = gameState.remainingTime;
                const totalDurationMs = gameState.duration;

                // Setze die sichtbare Restzeit und den Fortschrittsbalken sofort
                setTimeLeft(Math.max(0, Math.ceil(serverRemainingMs / 1000)));
                setProgress(Math.max(0, (serverRemainingMs / totalDurationMs) * 100));
                // Starte die Countdown-Logik.
                startCountdown(gameState.startTimestamp, totalDurationMs);
            } else if (gameState.hasStarted) {
                // Fallback, falls remainingTime nicht verfügbar ist (sollte nicht passieren)
                setCurrentIndex(gameState.questionIndex);
                startCountdown(gameState.startTimestamp, gameState.duration);
            }
            if (reveal) {
                setCorrectAnswer(reveal.correctAnswer);
                setRevealed(true);
            }
            setIsLoading(false);

        });
    }, [lobbyCode, persistentId, navigate, startCountdown, socket]);

    return {
        refs: { inputRef },
        state: {
            scoreboard, questions, currentIndex, isCreator, isSpectator, timeLeft,
            duration, progress, selectedAnswer, revealed, correctAnswer, gameOver,
            showFinalScreen, imageDifficulties, pixelationStrength, answerLocked,
            highlightedIndex, showJoinDialog, joinError, forcePassword, isLoading,
            suggestions, showSuggestions, socket, persistentId
        },
        actions: {
            setSelectedAnswer, setShowFinalScreen, setShowSuggestions,
            handleInputChange, handleKeyDown, handleSuggestionClick,
            handleSubmit, handleLeaveLobby, handleReturnToLobby, nextQuestion,
            setShowJoinDialog, setJoinError
        },
        lifecycle: { reconnect, startCountdown, setScoreboard, setRevealed, setCorrectAnswer, applyFullGameState }
    };
}
